import LearnLayout, { LEARN_PAGES } from './LearnLayout'

const page = LEARN_PAGES.find((p) => p.slug === 'excision-surgery')!

export default function ExcisionSurgeryPage() {
  return (
    <LearnLayout page={page}>
      <h2 id="two-procedures">Two procedures, one name</h2>
      <p>
        When a surgeon says they &ldquo;treat endometriosis surgically,&rdquo; they could mean one of
        two very different things. The difference matters more than almost anything else in your
        care.
      </p>
      <p>
        <strong>Ablation</strong> burns or vaporizes the surface of a lesion. It&rsquo;s faster,
        more common, and can be done by most gynecologic surgeons. But endometriosis often grows
        deeper than it looks. Ablation treats the visible top and leaves the roots behind, the way
        cutting a weed at the soil line leaves everything underground intact.
      </p>
      <p>
        <strong>Excision</strong> cuts the lesion out entirely, down to healthy tissue, and removes
        it. The surgeon develops a plane underneath the disease and lifts it out at the root.
        It&rsquo;s technically harder, takes longer, and requires real specialist training, but it
        is the only approach that removes the disease rather than scorching its surface.
      </p>

      <h2 id="why-root">Why the root is the whole point</h2>
      <p>
        Recall what makes endometriosis self-sustaining: lesions produce their own estrogen, recruit
        their own blood supply, and grow their own nerve fibers. A lesion that is burned on top but
        left in the wall still has its fuel loop and its blood supply. It can reactivate.
      </p>
      <p>
        This is reflected in the data patients feel in their own bodies: studies comparing the two
        techniques consistently show <strong>lower recurrence and longer-lasting pain relief</strong>{' '}
        after excision, particularly for deep infiltrating disease. When endometriosis comes roaring
        back a year after surgery, incomplete removal is frequently why.
      </p>

      <h2 id="deep-disease">Deep disease and why the surgeon matters</h2>
      <p>
        Endometriosis doesn&rsquo;t respect organ boundaries. Deep infiltrating disease can involve
        the uterosacral ligaments, the space between the rectum and vagina, the bowel, the bladder,
        and the ureters. Removing it safely sometimes means working alongside colorectal or urologic
        surgeons in the same operation.
      </p>
      <p>
        That is why the <strong>surgeon&rsquo;s experience is not a detail, it is the treatment.</strong>{' '}
        A high-volume excision specialist who operates on this disease every week is a fundamentally
        different proposition from a generalist who encounters it occasionally. This is exactly the
        gap Pelvia&rsquo;s referral network is built to close: connecting you to patient-preferred
        excision surgeons rather than leaving you to find one alone.
      </p>

      <h2 id="what-it-fixes">What surgery can and can&rsquo;t fix</h2>
      <p>
        Excision is the gold standard for the <strong>disease</strong>, and for many women it
        dramatically reduces pain, improves fertility prospects, and restores quality of life. But
        it is honest, and important, to know its limits.
      </p>
      <ul>
        <li>
          It removes lesions. It does not, by itself, reverse <strong>central sensitization</strong>{' '}
          (the nervous system&rsquo;s learned pain response), which is why some women
          benefit from pelvic floor physical therapy and pain-focused care alongside surgery.
        </li>
        <li>
          It treats endometriosis. It does not treat <strong>adenomyosis</strong>, which lives
          inside the uterine muscle and follows its own path.
        </li>
        <li>
          It is most durable in expert hands. The same operation done as ablation, or done
          incompletely, is far more likely to be repeated.
        </li>
      </ul>

      <div className="learn-bottom-line">
        <span className="learn-bottom-line-label">The bottom line</span>
        <p>
          Excision surgery removes endometriosis at the root, and in the hands of a trained
          specialist it offers the longest-lasting relief and the lowest recurrence of any option we
          have. It is not the right first step for everyone, and it works best as part of a plan
          rather than a single event. But for the disease itself, nothing else removes it.
          The single most important decision you make is <strong>who holds the scalpel.</strong>
        </p>
      </div>
    </LearnLayout>
  )
}
